/** @jsxImportSource @emotion/react */

import { ActiveSelection } from 'fabric/fabric-impl';
import { Group as GroupIcon, UngroupIcon } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import customization from 'modules/overlay-canvas/addons/customization';
import history from 'modules/overlay-canvas/addons/history';
import UDIconButton from 'ud-ui/components/icon-button';
import UDTippy from 'ud-ui/components/tippy';
import { selectActiveModal } from 'modules/template-editor/store/editor/selectors';
import { useEditorManager, useEditorActiveObjects, useEditorCanvasActions } from 'modules/template-editor/ui/context';

export function Group() {
  const { t } = useLingui();
  const { setActiveObjects } = useEditorCanvasActions();
  const activeModal = useSelector(selectActiveModal);
  const manager = useEditorManager();
  const activeObjects = useEditorActiveObjects();
  const isGroupedObjects = useMemo(() => {
    if (!manager) {
      return false;
    }
    return activeObjects.length === 1 && activeObjects[0].data?.type === 'group';
  }, [activeObjects, manager]);
  // const boardType = useGetBoardType();

  const toGroup = useCallback(() => {
    if (!manager) {
      return;
    }
    const activeObject = manager.canvas.getActiveObject();
    if (!activeObject) {
      return;
    }
    if (activeObject?.type !== 'activeSelection') {
      return;
    }

    const group = (activeObject as ActiveSelection).toGroup();
    group.data = { ...group.data, type: 'group' };
    group.set({ ...customization.getAllProperties() });
    manager.canvas.requestRenderAll();
    setActiveObjects([group]);
  }, [manager, setActiveObjects]);

  const toUngroup = useCallback(() => {
    if (!manager) {
      return;
    }
    const activeObject = manager.canvas.getActiveObject();
    if (!activeObject) {
      return;
    }

    const activeSelection = (activeObject as ActiveSelection).toActiveSelection();
    const objects = activeSelection.getObjects();
    objects.forEach((obj) => obj.set({ ...customization.getAllProperties() }));
    manager.canvas.requestRenderAll();
    setActiveObjects(objects);
  }, [manager, setActiveObjects]);

  const onClick = useCallback(() => {
    if (!manager) {
      return;
    }
    history.saveState();
    history.disableHistory();
    if (isGroupedObjects) {
      toUngroup();
    } else if (activeObjects.length > 1) {
      toGroup();
    }
    history.activeHistory();
  }, [activeObjects.length, isGroupedObjects, manager, toGroup, toUngroup]);

  const tooltipText = useMemo(() => {
    return isGroupedObjects ? t`Разгруппировать` : t`Группировать`;
  }, [isGroupedObjects, t]);

  return (
    <UDTippy disabled={Boolean(activeModal)} content={tooltipText} size="xs">
      <span style={{ display: 'inline-block' }}>
        <UDIconButton
          variant="ghost"
          icon={isGroupedObjects ? <UngroupIcon size={20} strokeWidth={1.5} /> : <GroupIcon size={20} strokeWidth={1.5} />}
          onClick={onClick}
          disabled={activeObjects.length < 2 && !isGroupedObjects}
          tabIndex={0}
        />
      </span>
    </UDTippy>
  );
}
